import memoize from 'lodash/memoize';
import {decodeHTML} from 'entities';

export type UrlLike = string | URL;

export type SafeLinkOptions = {
  /**
   * Additional list of allowed protocols.
   *
   * NOTE: this still doesn't allow for the use of `javascript:` protocol
   */
  allowedProtocols?: string[];

  /**
   * Which level the action (redirect) should be performed on.
   *
   * 'self' is default, and means the action is performed on the current browsing context.
   * 'top' means the action is performed on the top-level browsing context.
   * 'parent' means the action is performed on the parent browsing context of the current one.
   */
  target?: 'self' | 'top' | 'parent';

  /**
   * Allow non-dangerous arbitrary protocols (e.g. `my-app:`) to be used.
   *
   * NOTE: this stll doesn't allow for the use of `javascript:` protocol
   */
  UNSAFE_allowNonDangerousProtocols?: boolean;
};

// eslint-disable-next-line no-script-url
const DISALLOWED_PROTOCOLS = ['javascript:'];

const ALLOWED_PROTOCOLS = [
  'http:',
  'https:',
  'mailto:',
  'tel:',
  'vscode:',
  'swish:',
];
const pollutionPattern = `\\b(?:constructor|__defineGetter__|__defineSetter__|hasOwnProperty|__lookupGetter__|__lookupSetter__|isPrototypeOf|propertyIsEnumerable|toString|valueOf|__proto__|toLocaleString)\\b`;
const pollutionRegex = new RegExp(pollutionPattern, 'i');
/**
 * Retrieves the protocol from the URL
 * @example
 * ```
 * getUrlProtocol("https://amp.corp.stripe.com"); // returns 'https:'
 * ```
 * @param url
 */
const getUrlProtocol = (url: UrlLike) => {
  if (typeof url === 'string') {
    try {
      const u = new URL(url);
      return u.protocol;
    } catch {
      return;
    }
  }

  return url.protocol;
};

const isUrlProtocolAllowed = (
  url: UrlLike,
  allowedProtocols = ALLOWED_PROTOCOLS,
  // eslint-disable-next-line camelcase
  UNSAFE_allowNonDangerousProtocols = false,
): boolean => {
  const protocol = getUrlProtocol(url);
  if (!protocol) {
    return true;
  }

  if (DISALLOWED_PROTOCOLS.includes(protocol)) {
    return false;
  }

  return (
    // eslint-disable-next-line camelcase
    allowedProtocols.includes(protocol) || UNSAFE_allowNonDangerousProtocols
  );
};

const parseUrl = (url: string, base?: string) => {
  try {
    return new URL(url, base);
  } catch {
    return undefined;
  }
};

const tryParseURL = (u: UrlLike): URL | undefined => {
  if (typeof u === 'string') {
    const resolved =
      parseUrl(u) ??
      // e.g. when u = /path
      parseUrl(
        u,
        // In SSR context, location is not defined which causes this to throw an error.
        // We only use the value to run the sanity check on the URL and we don't return the value,
        // so it is safe to use a static URL if location is undefined. Using localhost in this case
        typeof location !== 'undefined' ? location.origin : 'http://localhost',
      );
    return resolved;
  }

  return undefined;
};

const containsProtoKeywords = (u: UrlLike) => {
  const parsedUrl = tryParseURL(u);
  if (!parsedUrl) {
    return false;
  }

  const urlParts = [parsedUrl.pathname, parsedUrl.search, parsedUrl.hash];
  return urlParts.some((part) => pollutionRegex.test(part));
};

export const isSafeUrl = memoize(
  (u: UrlLike, options?: SafeLinkOptions) => {
    // Prevent memory leaks by clearing the cache periodically
    if ((isSafeUrl.cache as Map<unknown, unknown>)?.size === 1000) {
      isSafeUrl.cache.clear?.();
    }

    const decodedUrl: UrlLike = typeof u === 'string' ? decodeHTML(u) : u;

    if (
      !isUrlProtocolAllowed(
        decodedUrl,
        [...ALLOWED_PROTOCOLS, ...(options?.allowedProtocols ?? [])],
        options?.UNSAFE_allowNonDangerousProtocols,
      )
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `[safe-links] redirect (${u}) blocked. URL has invalid protocol`,
      );
      return false;
    }

    if (containsProtoKeywords(decodedUrl)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[safe-links] redirect (${u}) blocked. URL contains proto keywords`,
      );
      return false;
    }

    return true;
  },
  (url, options) =>
    JSON.stringify([url, typeof location !== 'undefined', options]),
);

export const getTargetWindow = (options?: SafeLinkOptions) => {
  switch (options?.target) {
    case 'top':
      return window.top || window;
    case 'parent':
      return window.parent || window;
    default:
      // 'self' and undefined
      return window;
  }
};
